import { useEffect, useState, useCallback } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import httpClient from "api/httpClient";
import ConfirmationDialog from "components/Common/ConfirmationDialog";
import SchemaDialogForm from "components/Form/SchemaDialogForm";
import { agentFormSchema, agentRecord } from "./data/AgentSchema";
import AgentsTableData from "./data/AgentsTableData";
import { useTranslation } from "react-i18next";

export default function LoyaltyAgents() {
  const apiBase = process.env.REACT_APP_API_URL;
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchInput, setSearchInput] = useState("");

  const [selectedIds, setSelectedIds] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(agentRecord);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [editingRecord, setEditingRecord] = useState(agentRecord);

  const [itemToDelete, setItemToDelete] = useState(null);

  const [dialogTitle, setDialogTitle] = useState("Create Agent");

  const [confirmationDialogMessage, setConfirmationDialogMessage] = useState("");
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const [snack, setSnack] = useState({ open: false, color: "success", content: "" });

  const openSnack = (color, content) => setSnack({ open: true, color, content });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const loadAgents = useCallback(
    async (q = "") => {
      try {
        setLoading(true);
        const url = q
          ? `/agents/search-list?page=${page}&size=${rowsPerPage}&q=${encodeURIComponent(q)}`
          : `/agents?page=${page}&size=${rowsPerPage}`;

        const res = await httpClient.get(url);

        setItems(res.data.content || []);
        setTotalElements(res.data.totalElements || 0);
      } catch (err) {
        // use your handleApiError + snackbar pattern if you want
        openSnack("error", err?.response?.data?.message || err.message || "Failed to load agents");
      } finally {
        setLoading(false);
      }
    },
    [apiBase, page, rowsPerPage]
  );

  useEffect(() => {
    loadAgents(searchInput);
  }, [loadAgents, searchInput]);

  const openCreate = () => {
    setEditingRecord({ ...agentRecord });
    setDialogTitle("Create Agent");
    setDialogOpen(true);
  };

  const openEditDialog = (rec) => {
    setEditingRecord({ ...rec, password: "" });
    setDialogTitle("Edit Agent");
    setDialogOpen(true);
  };

  const submit = async (values) => {
    try {
      const payload = { ...values };
      if (!payload.password) delete payload.password;

      if (values.id) {
        await httpClient.put(`/agents/${values.id}`, payload);
        openSnack("success", "Agent updated");
      } else {
        await httpClient.post(`/agents`, payload);
        openSnack("success", "Agent created");
      }
      setDialogOpen(false);
      loadAgents(searchInput);
    } catch (e) {
      openSnack("error", e?.response?.data?.message || e.message || "Save failed");
    }
  };

  const onDeleteConfirmed = async () => {
    if (!itemToDelete) return;
    try {
      await httpClient.delete(`/agents/${itemToDelete.id}`);
      openSnack("success", "Agent deleted");
      setConfirmationDialogOpen(false);
      setItemToDelete(null);
      loadAgents(searchInput);
    } catch (e) {
      openSnack("error", e?.response?.data?.message || e.message || "Delete failed");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDBox alignItems="left" display="flex" gap={1}>
            <MDButton color="info" onClick={openCreate}>
              <Icon>add</Icon>&nbsp;Create
            </MDButton>
          </MDBox>
          <MDBox display="flex" gap={2} alignItems="center">
            <MDBox display="flex" gap={1}>
              <MDInput
                size="small"
                label="Search Agents"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyUp={(e) => e.key === "Enter" && loadAgents(searchInput)}
              />
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => loadAgents(searchInput)}
              >
                {t("search.go")}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>

        <Card>
          <MDBox p={2}>
            <AgentsTableData
              data={items}
              selectedIds={selectedIds}
              onToggle={(id) =>
                setSelectedIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                )
              }
              onEdit={openEditDialog}
              onDelete={(rec) => {
                setItemToDelete(rec);
                setConfirmationDialogMessage(`Delete ${rec.name}?`);
                setConfirmationDialogOpen(true);
              }}
            />
          </MDBox>
        </Card>
      </MDBox>

      <SchemaDialogForm
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={dialogTitle}
        initialRecord={editingRecord}
        schema={agentFormSchema}
        onSubmit={submit}
      />

      <ConfirmationDialog
        open={confirmationDialogOpen}
        title="Delete Agent"
        message={confirmationDialogMessage}
        description={`Delete agent ${toDelete?.sc_code || ""}?`}
        onCancel={() => setConfirmationDialogOpen(false)}
        onOk={onDeleteConfirmed}
      />

      <MDSnackbar
        color={snack.color}
        icon="notifications"
        title="Loyalty System"
        content={snack.content}
        open={snack.open}
        onClose={closeSnack}
        close={closeSnack}
        bgWhite
      />
    </DashboardLayout>
  );
}
